/* ============================================================
   EQUILIBRIUM USG — shared theme runtime
   Core behaviours every eq-* section can rely on. Page-specific
   behaviour lives in its own asset (eq-header.js, eq-shop.js …).
   ============================================================ */
(function () {
  'use strict';
  const EQ = (window.EQ = window.EQ || {});
  const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  EQ.reducedMotion = reduced;

  /* ---------- tiny event bus ---------- */
  const bus = document.createElement('span');
  EQ.on = (name, fn) => bus.addEventListener(name, fn);
  EQ.off = (name, fn) => bus.removeEventListener(name, fn);
  EQ.emit = (name, detail) => bus.dispatchEvent(new CustomEvent(name, { detail }));

  /* ---------- typewriter reveal on hover ----------
     Mark any element with data-eq-type="TEXT" (defaults to its own text).
     On mouseenter the text is retyped character by character with a
     blinking block caret trailing it. Hovering again restarts it. */
  const timers = new WeakMap();
  function typewrite(el) {
    if (reduced) return;
    // only retype leaf text — never a container, which would wipe its children
    if (el.children.length) return;
    const full = el.dataset.eqType || el.dataset.eqTypeFull || el.textContent;
    el.dataset.eqTypeFull = full;
    const speed = parseInt(el.dataset.eqTypeSpeed || '26', 10);
    let i = 0;
    clearInterval(timers.get(el));
    const caret = document.createElement('span');
    caret.className = 'eq-caret';
    const tick = () => {
      i++;
      el.textContent = full.slice(0, i);
      el.appendChild(caret);
      if (i >= full.length) {
        clearInterval(timers.get(el));
        setTimeout(() => { if (caret.parentNode === el) caret.remove(); }, 900);
      }
    };
    tick();
    timers.set(el, setInterval(tick, speed));
  }
  EQ.typewrite = typewrite;
  document.addEventListener('mouseenter', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const host = t.closest('[data-eq-type-host]');
    if (host) {
      host.querySelectorAll('[data-eq-type]').forEach(typewrite);
    } else if (t.matches('[data-eq-type]')) {
      typewrite(t);
    }
  }, true);

  /* ---------- money ---------- */
  EQ.money = function (cents) {
    const fmt = (window.EQ_MONEY_FORMAT || '${{amount}}');
    const n = (cents / 100).toFixed(2);
    const [i, d] = n.split('.');
    const withComma = i.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, withComma + '.' + d)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withComma)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, i.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + d)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, i.replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  };

  /* ---------- cart API ----------
     All cart mutations go through here. After each change the
     cart drawer section is re-rendered via the Section Rendering API
     and an `eq:cart` event is emitted with the fresh cart JSON. */
  const routes = () => window.EQ_ROUTES || { cart: '/cart', cart_add: '/cart/add', cart_change: '/cart/change', cart_update: '/cart/update' };
  const drawerSectionId = () => (window.EQ_CART_DRAWER_SECTION || 'eq-cart-drawer');

  async function refreshDrawer() {
    const id = drawerSectionId();
    try {
      // Section Rendering API. Not on /cart with Accept: application/json —
      // Shopify answers that with the cart JSON and no sections at all.
      const base = routes().root || '/';
      const res = await fetch(`${base}${base.indexOf('?') < 0 ? '?' : '&'}sections=${id}&t=${Date.now()}`);
      const json = await res.json();
      const html = json && json[id];
      const host = document.getElementById('eq-cart-drawer-root');
      if (html && host) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.getElementById('eq-cart-drawer-root');
        if (fresh) host.innerHTML = fresh.innerHTML;
      }
    } catch (e) { /* silent: drawer keeps last state */ }
  }

  async function getCart() {
    const res = await fetch(routes().cart + '.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function afterChange(cart) {
    await refreshDrawer();
    const c = cart && cart.items ? cart : await getCart();
    document.querySelectorAll('[data-eq-cart-count]').forEach((el) => {
      el.textContent = c.item_count;
      el.toggleAttribute('hidden', c.item_count === 0);
    });
    EQ.emit('eq:cart', c);
    return c;
  }

  EQ.cart = {
    get: getCart,
    refresh: () => afterChange(),
    async add(items, opts = {}) {
      const payload = Array.isArray(items) ? { items } : { items: [items] };
      const res = await fetch(routes().cart_add + '.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) { EQ.emit('eq:cart-error', json); throw json; }
      const cart = await afterChange();
      if (opts.open !== false) EQ.drawer.open('cart');
      return cart;
    },
    async change(line, quantity) {
      const res = await fetch(routes().cart_change + '.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity }),
      });
      const cart = await res.json();
      return afterChange(cart);
    },
    async changeKey(id, quantity) {
      const res = await fetch(routes().cart_change + '.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id, quantity }),
      });
      const cart = await res.json();
      return afterChange(cart);
    },
    async note(note) {
      await fetch(routes().cart_update + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }) });
    },
  };

  /* ---------- drawers / overlays ----------
     Any element with data-eq-drawer="name" is a drawer. Open with
     EQ.drawer.open(name); [data-eq-open="name"] / [data-eq-close] buttons
     wire themselves. Body gets .eq-drawer-open while any is open. */
  const openSet = new Set();
  function sync() { document.body.classList.toggle('eq-drawer-open', openSet.size > 0); }
  EQ.drawer = {
    open(name) {
      const el = document.querySelector(`[data-eq-drawer="${name}"]`);
      if (!el) return;
      el.setAttribute('data-open', '');
      el.removeAttribute('hidden');
      el.setAttribute('aria-hidden', 'false');
      openSet.add(name); sync();
      const focus = el.querySelector('[data-eq-autofocus], input, button, a');
      if (focus) setTimeout(() => focus.focus({ preventScroll: true }), 50);
      EQ.emit('eq:drawer-open', { name });
    },
    close(name) {
      const els = name ? [document.querySelector(`[data-eq-drawer="${name}"]`)] : [...document.querySelectorAll('[data-eq-drawer][data-open]')];
      els.forEach((el) => {
        if (!el) return;
        el.removeAttribute('data-open');
        el.setAttribute('aria-hidden', 'true');
        openSet.delete(el.getAttribute('data-eq-drawer'));
        EQ.emit('eq:drawer-close', { name: el.getAttribute('data-eq-drawer') });
      });
      sync();
    },
    toggle(name) {
      const el = document.querySelector(`[data-eq-drawer="${name}"]`);
      if (!el) return;
      el.hasAttribute('data-open') ? EQ.drawer.close(name) : EQ.drawer.open(name);
    },
  };
  document.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const opener = t.closest('[data-eq-open]');
    if (opener) { e.preventDefault(); EQ.drawer.open(opener.getAttribute('data-eq-open')); return; }
    const toggler = t.closest('[data-eq-toggle]');
    if (toggler) { e.preventDefault(); EQ.drawer.toggle(toggler.getAttribute('data-eq-toggle')); return; }
    const closer = t.closest('[data-eq-close]');
    if (closer) { e.preventDefault(); EQ.drawer.close(closer.getAttribute('data-eq-close') || undefined); return; }
    if (t.matches('[data-eq-drawer][data-open]')) { EQ.drawer.close(t.getAttribute('data-eq-drawer')); }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && openSet.size) EQ.drawer.close(); });

  /* ---------- accordion (symmetrical 460ms, grid-template-rows) ----------
     <div data-eq-acc><button data-eq-acc-btn aria-expanded>…</button><div data-eq-acc-panel><div>…</div></div></div> */
  document.addEventListener('click', (e) => {
    const btn = e.target instanceof Element ? e.target.closest('[data-eq-acc-btn]') : null;
    if (!btn) return;
    const acc = btn.closest('[data-eq-acc]');
    if (!acc) return;
    const open = acc.hasAttribute('data-open');
    const group = acc.getAttribute('data-eq-acc-group');
    if (group && !open) {
      document.querySelectorAll(`[data-eq-acc][data-eq-acc-group="${group}"][data-open]`).forEach((o) => {
        o.removeAttribute('data-open');
        const b = o.querySelector('[data-eq-acc-btn]'); if (b) b.setAttribute('aria-expanded', 'false');
      });
    }
    acc.toggleAttribute('data-open', !open);
    btn.setAttribute('aria-expanded', String(!open));
  });

  /* ---------- quantity steppers ----------
     <div data-eq-qty><button data-eq-qty-dec>−</button><input …><button data-eq-qty-inc>+</button></div> */
  document.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const dec = t.closest('[data-eq-qty-dec]'), inc = t.closest('[data-eq-qty-inc]');
    if (!dec && !inc) return;
    const host = t.closest('[data-eq-qty]');
    const input = host && host.querySelector('input');
    if (!input) return;
    const min = parseInt(input.min || '1', 10), max = input.max ? parseInt(input.max, 10) : Infinity;
    let v = parseInt(input.value || '1', 10) + (inc ? 1 : -1);
    v = Math.max(min, Math.min(max, v));
    input.value = v;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- scroll state (header compaction) ---------- */
  let ticking = false;
  const onScroll = () => {
    if (ticking) return; ticking = true;
    requestAnimationFrame(() => {
      document.body.classList.toggle('eq-scrolled', window.scrollY > 24);
      ticking = false;
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* ---------- initial cart count ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-eq-cart-count]').forEach((el) => {
      if (el.textContent.trim() === '0') el.setAttribute('hidden', '');
    });
  });
})();
